import { useNavigate } from "react-router-dom";
import DOMPurify from "isomorphic-dompurify";
import { AlertTriangle, Info, Lightbulb } from "lucide-react";
import { CompareTable } from "../info/CompareTable";
import { FaqItems } from "../faq/FaqItems";
import { ArticleCta } from "./ArticleCta";
import { ArticleFigure, ArticleGallery } from "./ArticleMedia";

const SANITIZE = { ALLOWED_TAGS: ["p", "h2", "h3", "strong", "em", "b", "i", "u", "s", "ul", "ol", "li", "a", "br", "blockquote"], ALLOWED_ATTR: ["href", "title"] };
const strip = (h) => h.replace(/<[^>]+>/g, "").replace(/&amp;/g, "&").trim();
export const headingId = (t) => t.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

// Gesaneerde HTML uit de editor: koppen krijgen een anker, externe links openen in een nieuw tabblad.
const prepare = (html = "") => DOMPurify.sanitize(html, SANITIZE)
  .replace(/<h2>(.*?)<\/h2>/g, (_, inner) => `<h2 id="${headingId(strip(inner))}" class="scroll-mt-28">${inner}</h2>`)
  .replace(/<a href="(https?:[^"]+)"/g, '<a href="$1" target="_blank" rel="noopener noreferrer"');

export const articleHeadings = (blocks = []) => blocks.filter((b) => b.type === "text")
  .flatMap((b) => [...(b.html || "").matchAll(/<h2>(.*?)<\/h2>/g)].map((m) => ({ id: headingId(strip(m[1])), text: strip(m[1]) })))
  .filter((h) => h.text);

const Html = ({ html, className = "", testid }) => <div className={`prose-kozijn ${className}`} data-testid={testid} dangerouslySetInnerHTML={{ __html: prepare(html) }} />;

const TIP = { tip: [Lightbulb, "border-maas/30 bg-maas-soft/60", "text-maas"], let_op: [AlertTriangle, "border-gold/40 bg-gold-soft/50", "text-gold-hover"], info: [Info, "border-slate-200 bg-slate-50", "text-slate-600"] };
const TipBox = ({ b, i }) => {
  const [Icon, box, tone] = TIP[b.variant] || TIP.tip;
  return (
    <aside className={`my-8 flex gap-4 rounded-2xl border p-5 sm:p-6 ${box}`} data-testid={`kb-block-tip-${i}`}>
      <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${tone}`} aria-hidden="true" />
      <div className="min-w-0">
        {b.title && <p className="text-[16px] font-semibold text-ink">{b.title}</p>}
        <Html html={b.html} className="mt-1 text-[15.5px] [&_p:last-child]:mb-0" />
      </div>
    </aside>
  );
};

const tableBody = (b) => [b.head, ...b.rows].map((r) => r.map((c) => (c || "–").replace(/\|/g, "/")).join(" | ")).join("\n");

const BlockView = ({ b, i }) => {
  if (b.type === "text") return <Html html={b.html} testid={`kb-block-text-${i}`} />;
  if (b.type === "image") return b.image?.path ? <ArticleFigure b={b} i={i} /> : null;
  if (b.type === "gallery") return b.images?.length ? <ArticleGallery b={b} i={i} /> : null;
  if (b.type === "tip") return <TipBox b={b} i={i} />;
  if (b.type === "cta") return <ArticleCta b={b} i={i} />;
  if (b.type === "table") return b.head?.length && b.rows?.length ? (
    <div className="my-10" data-testid={`kb-block-table-${i}`}>
      {b.title && <h3 className="mb-4 text-[20px] font-semibold text-ink">{b.title}</h3>}
      <CompareTable body={tableBody(b)} caption={b.title || "Vergelijking"} />
      {b.note && <p className="mt-3 text-[13.5px] text-slate-500">{b.note}</p>}
    </div>
  ) : null;
  if (b.type === "faq") return b.faqs?.length ? (
    <section className="my-10" data-testid={`kb-block-faq-${i}`}>
      <h2 className="mb-2 text-2xl font-bold text-ink sm:text-3xl">{b.title || "Veelgestelde vragen"}</h2>
      <FaqItems faqs={b.faqs} testid={`kb-block-faq-${i}-item`} />
    </section>
  ) : null;
  return null;
};

export const ArticleBlocks = ({ blocks = [] }) => {
  const navigate = useNavigate();
  const onClick = (e) => {
    const href = e.target.closest?.(".prose-kozijn a")?.getAttribute("href");
    if (href?.startsWith("/")) { e.preventDefault(); navigate(href); }
  };
  return <div className="text-[16.5px]" onClick={onClick} data-testid="kb-article-body">{blocks.map((b, i) => <BlockView key={b.id || i} b={b} i={i} />)}</div>;
};
